import { Card } from '@/components/ui/card';
import { Gauge } from 'lucide-react';
import type { TokenUsageDto } from '../models/types';

interface TokenUsageCardProps {
  tokenUsage: TokenUsageDto;
}

export const TokenUsageCard: React.FC<TokenUsageCardProps> = ({ tokenUsage }) => {
  const formatNumber = (num: number): string => {
    return (num ?? 0).toLocaleString('en-US');
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount ?? 0);
  };

  // Handle both PascalCase (API) and camelCase (expected) field names
  const t = tokenUsage as any;
  const totalAllocated = t.totalTokensAllocated ?? t.TotalTokensIncluded ?? 0;
  const tokensUsed = t.tokensUsed ?? t.TokensUsedThisMonth ?? 0;
  const tokensRemaining = t.tokensRemaining ?? (totalAllocated - tokensUsed);
  const usagePercentage = t.usagePercentage ?? (totalAllocated > 0 ? (tokensUsed / totalAllocated) * 100 : 0);
  const overage = t.overage ?? Math.max(0, tokensUsed - totalAllocated);
  const estimatedCost = t.estimatedCost ?? (overage > 0 ? overage * 0.00001 : 0);
  const employeeBreakdown = t.employeeBreakdown ?? t.UsageBreakdown ?? [];

  return (
    <Card>
      <div className="flex items-center gap-3 p-4 pb-0">
        <Gauge className="h-6 w-6 text-accent-text" />
        <div>
          <p className="text-sm font-semibold text-foreground">Token Usage</p>
          <span className="text-xs text-muted-foreground">
            {formatNumber(tokensRemaining)} of {formatNumber(totalAllocated)} remaining
          </span>
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        <div className="h-2 w-full rounded-full bg-secondary" style={{ marginBottom: '16px' }}>
          <div
            className="h-full rounded-full bg-primary"
            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <span className="block text-xs text-muted-foreground">Used</span>
            <p className="text-sm">{formatNumber(tokensUsed)}</p>
          </div>
          <div>
            <span className="block text-xs text-muted-foreground">Overage</span>
            <p className="text-sm">{overage > 0 ? formatNumber(overage) : '-'}</p>
          </div>
          <div>
            <span className="block text-xs text-muted-foreground">Usage %</span>
            <p className="text-sm">{usagePercentage.toFixed(1)}%</p>
          </div>
          <div>
            <span className="block text-xs text-muted-foreground">Estimated Cost</span>
            <p className="text-sm">{formatCurrency(estimatedCost)}</p>
          </div>
        </div>

        {Array.isArray(employeeBreakdown) && employeeBreakdown.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <span className="block text-xs font-semibold text-muted-foreground">Top Consumers</span>
            {employeeBreakdown.slice(0, 3).map((emp: any) => (
              <div
                key={emp.employeeId ?? emp.EmployeeId}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '4px 0',
                }}
              >
                <span className="text-xs text-muted-foreground">{emp.employeeName ?? emp.EmployeeName}</span>
                <span className="text-xs text-muted-foreground">{formatNumber(emp.tokensUsed ?? emp.TokensUsed ?? 0)} ({(emp.percentageOfTotal ?? 0).toFixed(1)}%)</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
